import HTMLEditor from 'components/controls/WysiwygEditor';
import UploadFile from 'components/form/UploadFile';
import HelpIcon from 'components/icons/HelpIcon';
import { YesNoDataSource } from 'components/v2/compiler/constants';
import { EPD_TEMPLATES_URL, MASS_FLOW_PROPERTY_DEFAULT_CONVERSION_FACTOR, ProcessStatus } from 'constants/constants';
import arrowDown from 'images/arrow-down.png';
import arrowUp from 'images/arrow-up.png';
import helpIcon from 'images/icons/svg/info.svg';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import CollectionService from 'services/CollectionService';
import CpcService from 'services/CpcService';
import {
  ActionState,
  CollectionListModel,
  CollectionModel,
  ConversionFactorToMassModel,
  EPDModel,
  GeographicalScopeModel,
  LanguageModel,
  OkobaudatProductCategoryModel,
  Subtype,
  UploadDocumentTypesModel,
} from 'services/EpdClient';
import EpdService from 'services/EpdService';
import EPDService from 'services/EpdService';
import LanguagesService from 'services/LanguagesService';
import UploadDocumentTypesService from 'services/UploadDocumentTypesService';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { useDictionaryValues, useReferencePackageVersions15804 } from 'services/api/queries';
import styled from 'styled-components';
import { useTheme } from 'styled-components';
import { A, H3, InfoBox, Label } from 'styles/Styles.styled';
import { DictionaryValue, EPDBasedOnUnitEnum, Option } from 'types/types';
import { reactSelectPropsForHtmlLabel } from 'util/reactSelectPropsForHtmlLabel';
import { getConformityStandardOptions, getSelectedStandardOptions } from 'util/conformityStandards';
import { getOptionsFromDictionary } from 'util/utils';

import { customToolbarEpd } from '../../components/form/customToolbar';
import { ImagesUpload } from './ImagesUpload';
import { Validation } from './Validation';
import { FixedLabel, FixedLabelTop, SelectWrapper } from './styles';
import { WizardStepValidationSchema } from './types';
import { FixedTextInputComponent } from 'components/form/FixedTextInputComponent';
import EpdSelectComponent from 'components/form/EpdSelectComponent';
import FixedTextAreaComponent from 'components/form/FixedTextAreaComponent';
import { SubTypeInfo } from '../../components/wizard/step2/SubTypeInfo';
import { TechInfo } from '../../components/wizard/step2/TechInfo';
import { ReferenceInfo } from '../../components/wizard/step2/ReferenceInfo';
import { GtinInfo } from '../../components/wizard/step2/GtinInfo';
import {
  ArrowDownBox,
  ArrowImage,
  ArrowUpBox,
  Box,
  Column,
  FixedRow,
  ReferenceColumn,
  SelectStyle,
} from '../../components/wizard/styles';
import MaterialPropertiesSection from '../../components/wizard/step2/MaterialPropertiesSection';
import DatasourcesSection from 'components/wizard/step2/DatasourcesSection';
import WizardStep2LcaResults from './sections/WizardStep2LcaResults';
import { ValidationIconWithContainer } from 'components/v2/form/styled';
import ConformityStandardsInfoBox from 'components/v2/conformity-standards/ConformityStandardsInfoBox';

type SubtypeOption = {
  label: string;
  value: Subtype | undefined;
};

type FileUploaderType = 'documents' | 'productImages' | 'lca';

const WizardStep2: React.FunctionComponent<{
  epd?: EPDModel;
  onChangeEpd?: any;
  onUploadFile(field: string, file: File, documentTypeId: string | null, documentLanguageId: string | null): void;
  onRemoveFile(fileId: string): void;
  validation: WizardStepValidationSchema;
  setUpdatingEpd: any;
  isFetching: boolean;
  collectionList: CollectionModel[];
  isLoadingCollections?: boolean;
  setUpdatedEpd: React.Dispatch<React.SetStateAction<EPDModel | undefined>>;
}> = ({
  epd,
  onChangeEpd,
  onUploadFile,
  onRemoveFile,
  validation,
  setUpdatingEpd,
  isFetching,
  collectionList,
  isLoadingCollections,
  setUpdatedEpd,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [locationList, setLocationList] = React.useState<Option[]>([]);
  const [lookupUNCPCCodes, setLookupUNCPCCodes] = useState<Option[]>([]);
  const referencePackageVersions15804 = useReferencePackageVersions15804().data;
  const [languageList, setLanguages] = React.useState<LanguageModel[]>([]);
  const [documentTypeList, setDocumentTypes] = React.useState<UploadDocumentTypesModel[]>([]);
  const [fileUploaders, setFileUploaders] = React.useState<FileUploaderType[]>([]);

  const [infoArrowItems, setInfoArrowItems] = useState<any>({
    gtin: false,
    geo: false,
    referenceFlows: false,
    biogenicCarbon: false,
    technology: false,
    technicalPurpose: false,
    subType: false,
    materialProperties: false,
    dataSources: false,
    useForDatasets: false,
  });
  const [conformityStandardsOptions, setConformityStandardsOptions] = React.useState<Option[]>([]);

  const [biogenicCarbonContentUnits, setBiogenicCarbonContentUnits] = React.useState<Option[]>([]);

  const [okobaudatProductCategories, setOkobaudatProductCategories] = React.useState<OkobaudatProductCategoryModel[]>([]);

  const selectedProductCategory = okobaudatProductCategories?.find((c) => c.id === epd?.okobaudatProductCategoryId);
  const selectedSubCategory = selectedProductCategory?.children?.find((c) => c.id === epd?.okobaudatProductSubCategoryId);

  const selectedFlowPropertyId = epd?.referenceFlow?.flowPropertyId ?? null;
  const conversionFactorToMassValue = epd?.conversionFactorToMass?.conversionFactor ?? null;
  const selectedConversionFactor = epd?.referenceFlow?.conversionFactor ?? null;

  const [selectedFunctionalUnitDescription, setSelectedFunctionalUnitDescription] = useState<string | undefined>(
    epd?.functionalUnitDescription
  );

  const getSubTypeOptionLabel = (key: string) => {
    switch (key) {
      case Subtype.Specific:
        return 'Specific (single product)';
      case Subtype.Average:
        return 'Average (multiple products)';
      case Subtype.Representative:
        return 'Representative (Sector EPD)';
      case Subtype.Template:
        return 'Template (product not yet on the market)';
      default:
        return '';
    }
  };

  const getSubTypeOptionDisabled = (key: string) => {
    switch (key) {
      case Subtype.Average:
        return epd?.isSectorEPD;
      case Subtype.Specific:
        return epd?.isSectorEPD;
      default:
        return false;
    }
  };

  let subtypeOptions: any[] = [];
  for (let s in Subtype) {
    subtypeOptions.push({ value: s, label: getSubTypeOptionLabel(s), isDisabled: getSubTypeOptionDisabled(s) });
  }

  const hiddenEditorStyle = {
    padding: '0 1rem',
    fontSize: '14px',
    width: '100%',
    height: '20vh',
    background: theme.colors.lightGray,
    color: theme.colors.elementDisabledTextColor,
  };

  const visibleEditorStyle = {
    border: '1px solid grey',
    padding: '0 1rem',
    fontSize: '14px',
    height: '20vh',
  };

  const productFlowInput = {
    margin: 0,
    borderRadius: 0,
    borderColor: 'gray',
    borderWidth: '1px',
    paddingLeft: '8px',
    boxSizing: 'border-box',
    width: '100%',
    height: '49px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'no-wrap',
    '-ms-text-overflow': 'ellipsis',
    '-o-text-overflow': 'ellipsis',
  };

  const toggleOpen = (item: any) => {
    const oldValue = infoArrowItems[item];
    const newObj = { ...infoArrowItems, [item as any]: !oldValue };
    setInfoArrowItems(newObj);
  };

  useEffect(() => {
    const fetchLocations = async () => {
      const result = await EpdService.getLocations();
      setLocationList(
        result.map((x: GeographicalScopeModel) => {
          return { value: x.id, label: x.name } as Option;
        })
      );
    };
    const fetchLanguages = async () => {
      const result = await LanguagesService.getLanguages();
      setLanguages(result);
    };
    const fetchDocumentType = async () => {
      const res = await UploadDocumentTypesService.getDocumentTypes();
      setDocumentTypes(res);
      return res;
    };

    const fetchUNCPCCodes = async () => {
      const result = await CpcService.getCpcs(undefined, undefined, undefined);
      setLookupUNCPCCodes(
        result.map((x) => {
          return { label: x.description, value: x.id } as Option;
        })
      );
    };

    const fetchConformityStandardsOptions = async () => {
      const result = await EpdService.getConformityStandards();
      setConformityStandardsOptions(getConformityStandardOptions(result, epd?.pcr));
    };

    const fetchBiogenicCarbonContentUnits = async () => {
      const result = await EpdService.getBiogenicCarbonContentUnits();
      setBiogenicCarbonContentUnits(
        result.map(
          (x: DictionaryValue) =>
            ({
              value: x.key.toString(),
              label: x.value,
            } as Option)
        )
      );
    };

    const fetchOkobaudatProductCategories = async () => {
      const result = await EpdService.getOkobaudatProductCategories();
      setOkobaudatProductCategories(result);
    };

    fetchUNCPCCodes();
    fetchLanguages();
    fetchDocumentType();
    fetchLocations();
    fetchConformityStandardsOptions();
    fetchBiogenicCarbonContentUnits();
    fetchOkobaudatProductCategories();
  }, []);

  const uploadFile = async (
    file: File | undefined,
    propertyName: string,
    documentTypeId: string | null,
    documentLanguageId: string | null,
    fileUploader: FileUploaderType
  ) => {
    if (!file) {
      return;
    }

    try {
      setFileUploaders((old) => [...old, fileUploader]);
      await onUploadFile(propertyName, file, documentTypeId, documentLanguageId);
    } finally {
      setFileUploaders((old) => {
        const index = old.indexOf(fileUploader);
        if (index === -1) {
          throw new Error('Uploader was removed more than once.');
        }
        return old.filter((_, i) => i !== index);
      });
    }
  };

  const dictionariesQuery = useDictionaryValues();

  const productFlowOptions =
    dictionariesQuery.data
      ?.filter((d) => d.dictionaryCode === EPD_DICTIONARY_CODES.REFERENCE_FLOW_UNIT)
      .map((d) => ({ value: d.value.toLowerCase(), label: d.description })) ?? [];

  const epdBasedOnUnitOptions = getOptionsFromDictionary(dictionariesQuery.data, EPD_DICTIONARY_CODES.EPD_BASED_ON_UNIT);

  const selectedEPDBasedOnUnit = epdBasedOnUnitOptions.find(
    (x) => epd?.epdBasedOnUnit && x.value === Object.keys(EPDBasedOnUnitEnum).indexOf(epd?.epdBasedOnUnit).toString()
  );

  const productTypeOptions = getOptionsFromDictionary(dictionariesQuery.data, EPD_DICTIONARY_CODES.PRODUCT_TYPE);

  const selectedProductTypeOriginal = !isNaN(Number(epd?.productType))
    ? productTypeOptions.find((x) => x.value == epd?.productType)
    : productTypeOptions.find((x) => x.label == epd?.productType);

  const selectedProductType = selectedProductTypeOriginal
    ? ({
        value: selectedProductTypeOriginal?.value.toString(),
        label: selectedProductTypeOriginal?.label,
      } as Option)
    : null;

  const collectionOptions: Option[] = (collectionList ?? [])
    .filter((c): c is CollectionModel & { id: string; name: string } => !!c?.id && !!c?.name)
    .map((c) => ({ value: String(c.id), label: c.name! }));

  const selectedCollections: Option[] = collectionOptions.filter((opt) =>
    (epd?.collections ?? []).some((c) => String(c?.id ?? '') === opt.value)
  );

  const changeCollection = async (newCollections: MultiValue<Option>) => {
    try {
      setUpdatingEpd(ProcessStatus.Fetching);

      const oldIds = (epd?.collections ?? []).map((c) => String(c?.id ?? ''));
      const newIds = (newCollections as Option[]).map((o) => o.value);

      const toAdd = newIds.filter((id) => !oldIds.includes(id));
      const toRemove = oldIds.filter((id) => !newIds.includes(id));

      await Promise.all([
        ...toAdd.map((id) => CollectionService.addCollectionEPD(id, String(epd?.id))),
        ...toRemove.map((id) => CollectionService.removeCollectionEpd(id, String(epd?.id))),
      ]);

      const updatedCollections: CollectionListModel[] = newIds
        .map((id) => {
          const full = collectionList.find((c) => String(c?.id ?? '') === id);
          return full?.id ? ({ id: full.id, name: full.name } as CollectionListModel) : undefined;
        })
        .filter((x): x is CollectionListModel => !!x);

      setUpdatedEpd((prev) => (prev ? { ...prev, collections: updatedCollections } : prev));
      setUpdatingEpd(ProcessStatus.Success);
    } catch {
      setUpdatingEpd(ProcessStatus.Error);
    }
  };

  const uploadDocumentFile = async (
    e: ChangeEvent<HTMLInputElement>,
    propertyName: string,
    docTypeId: string,
    docLangId: string
  ) => {
    const target = e.target as HTMLInputElement;
    let file = target && target.files && target.files[0];

    if (!file || !epd) {
      return null;
    }
    if (docTypeId !== '2489c33d-56f6-4fe7-b5cd-a94e82900708') {
      propertyName = 'DocumentOther';
    }

    await uploadFile(file, propertyName, docTypeId, docLangId, 'documents');
  };

  const changeLocation = (locations?: MultiValue<Option>) => {
    if (Array.isArray(locations)) {
      const selectedLocations = locations.map((x) => x.value);
      onChangeEpd('GeographicalScopes', selectedLocations);
    } else {
      onChangeEpd('GeographicalScopes', undefined);
    }
  };

  const handleChangestatementOfConformityWith = (StatementOfConformityWithStandards?: MultiValue<Option>) => {
    if (Array.isArray(StatementOfConformityWithStandards)) {
      const selectedStandards = StatementOfConformityWithStandards.map((x) => x.value);
      onChangeEpd('statementOfConformityWithStandards', selectedStandards);
    } else {
      onChangeEpd('statementOfConformityWithStandards', undefined);
    }
  };

  const changeSubtype = async (subType?: SingleValue<SubtypeOption>) => {
    if (subType) {
      const selectedSubType = subType.value;
      onChangeEpd('subtype', selectedSubType);
    }
  };

  const createFlowModel = (name: string | null, flowPropertyId: string | null, conversionFactor: number | null) => {
    return {
      name,
      flowPropertyItem:
        flowPropertyId || conversionFactor
          ? {
              flowPropertyId: flowPropertyId,
              conversionFactor: conversionFactor,
            }
          : null,
    };
  };

  const changeProductFlowUnit = async (
    name: string | null,
    newFlowPropertyId: string | null,
    conversionFactor: number | null
  ) => {
    const newUnitIsMass = productFlowOptions.find((o) => o.value === newFlowPropertyId)?.label === 'Mass (kg)';
    const previousUnitIsMass = productFlowOptions.find((o) => o.value === selectedFlowPropertyId)?.label === 'Mass (kg)';
    if (newUnitIsMass && !previousUnitIsMass) {
      const factor = createConversionFactorToMassModel(MASS_FLOW_PROPERTY_DEFAULT_CONVERSION_FACTOR);
      await onChangeEpd('conversionFactorToMass', factor);
    } else if (!newUnitIsMass && previousUnitIsMass) {
      const factor = createConversionFactorToMassModel(null);
      await onChangeEpd('conversionFactorToMass', factor);
    }

    if (newUnitIsMass) {
      await onChangeEpd('massOfDeclaredUnit', conversionFactor);
    }

    const newValue = createFlowModel(name, newFlowPropertyId, conversionFactor);

    await onChangeEpd('productFlow', newValue);
  };

  const changeProductFlowValue = async (
    name: string | null,
    flowPropertyId: string | null,
    conversionFactor: number | null
  ) => {
    const isMassUnit = productFlowOptions.find((o) => o.value === flowPropertyId)?.label === 'Mass (kg)';
    if (isMassUnit) {
      await onChangeEpd('massOfDeclaredUnit', (conversionFactor as any) === '' ? null : conversionFactor);
    }

    const newValue = createFlowModel(name, flowPropertyId, conversionFactor);
    await onChangeEpd('productFlow', newValue);
  };

  const createConversionFactorToMassModel = (conversionFactor: number | null): ConversionFactorToMassModel => {
    return {
      conversionFactor: conversionFactor,
    };
  };

  const changeEPDBasedOnUnit = (epdBasedOnUnit?: any, functionalUnitDescription?: string) => {
    const newValue: any = {
      epdBasedOnUnit: epdBasedOnUnit,
      functionalUnitDescription: functionalUnitDescription,
    };

    return newValue;
  };

  const geoScopes =
    epd?.geographicalScopes &&
    epd?.geographicalScopes.map((x) => {
      return { label: x.name, value: x.id } as Option;
    });

  function handleNumericFieldChange(event: any) {
    const { value } = event.target;
    const formattedProductFlowValue = value.replace(/^0+(?=\d)/, ''); // Removes leading zeros, except one before a period
    event.target.value = formattedProductFlowValue;
  }

  const handleNumericFieldKeyDown = (event: any) => {
    const {
      code,
      target: { value },
    } = event;
    const fractionalNumWithOneDigitPattern = /^0\.\d$/;
    if (code === 'Backspace' && fractionalNumWithOneDigitPattern.test(value)) {
      event.target.value = '';
      return;
    }
    if (code !== 'Minus') {
      return;
    }
    event.preventDefault();
  };

  const handleNumericFieldPaste = (event: any) => {
    event.preventDefault();
    const clipboardData = event.clipboardData || window.Clipboard;
    const pastedValue = clipboardData.getData('text');

    if (pastedValue === '0') {
      // Inserts the text at the current caret position
      document.execCommand('insertText', false, '0.01');
      return;
    }

    let formattedProductFlowValue = pastedValue
      .replace(/^0+(?=\d)/, '') // Removes leading zeros, except one before a period
      .replace(/-/g, ''); // Removes minuses

    document.execCommand('insertText', false, formattedProductFlowValue);
  };

  const handleBiogenicKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // First apply existing minus-key prevention and backspace handling
    handleNumericFieldKeyDown(event);
    if (event.defaultPrevented) return;

    // Hard cap: block digit input when already at 8 decimal places
    const { key, target, shiftKey, ctrlKey, metaKey } = event;
    if (ctrlKey || metaKey) return;
    const value = (target as HTMLInputElement).value;

    // Block ArrowDown when value would become negative
    if (key === 'ArrowDown') {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue) && numericValue <= 0) {
        event.preventDefault();
        return;
      }
    }

    const selectionStart = (target as HTMLInputElement).selectionStart ?? value.length;
    const selectionEnd = (target as HTMLInputElement).selectionEnd ?? value.length;
    const isDigit = key.length === 1 && key >= '0' && key <= '9';
    if (!isDigit) return;

    // Calculate what the value will look like after the keypress (considering selection)
    const nextValue = value.slice(0, selectionStart) + key + value.slice(selectionEnd);
    const dotIndex = nextValue.indexOf('.');
    if (dotIndex !== -1 && nextValue.length - dotIndex - 1 > 8) {
      event.preventDefault();
    }
  };

  const handleBiogenicPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const clipboardData = event.clipboardData || (window as any).Clipboard;
    const pastedValue = clipboardData.getData('text');

    let formatted = pastedValue
      .replace(/^0+(?=\d)/, '') // Removes leading zeros, except one before a period
      .replace(/-/g, ''); // Removes minuses

    // Truncate to 8 decimal places
    const dotIndex = formatted.indexOf('.');
    if (dotIndex !== -1 && formatted.length - dotIndex - 1 > 8) {
      formatted = formatted.slice(0, dotIndex + 9);
    }

    event.currentTarget.value = formatted;
  };

  const referencePackageVersions15804Options: Option[] =
    referencePackageVersions15804?.versions.map((r) => ({
      label: r.name,
      value: r.id,
    })) ?? [];

  const changeBiogenicCarbonContentValues = (name: string, value?: string) => {
    switch (name) {
      case 'productItemUnitKey':
        return {
          ...epd?.biogenicCarbonContent,
          productItem: { ...epd?.biogenicCarbonContent?.productItem, unitKey: value },
        };
      case 'productItemConversionFactor':
        return {
          ...epd?.biogenicCarbonContent,
          productItem: { ...epd?.biogenicCarbonContent?.productItem, conversionFactor: value },
        };
      case 'accompanyingPackageItemUnitKey':
        return {
          ...epd?.biogenicCarbonContent,
          accompanyingPackageItem: { ...epd?.biogenicCarbonContent?.accompanyingPackageItem, unitKey: value },
        };
      case 'accompanyingPackageItemConversionFactor':
        return {
          ...epd?.biogenicCarbonContent,
          accompanyingPackageItem: { ...epd?.biogenicCarbonContent?.accompanyingPackageItem, conversionFactor: value },
        };
      default:
        return { ...epd?.biogenicCarbonContent, [name]: value };
    }
  };

  const { statementOfConformityWithStandards } = epd || {};

  // #19675: the information box is shown only while the field is editable for this user and state
  const showConformityStandardsInfo = epd?.statementOfConformityWithStandardsState.actionState === ActionState.Enabled;
  const conformityStandardsLabel = (
    <>
      Standards conformance
      <HelpIcon content={'Select the standards with which the EPD is compliant.'} />
      <Validation validation={validation} epd={epd} field="statementOfConformityWithStandards" />
    </>
  );

  return (
    <div>
      <H3 style={{ marginTop: 0 }}>{t('epdWizard.wizardStep2.properties.infoPublic')}</H3>

      <FixedRow state={epd?.documentsState.actionState}>
        <FixedLabelTop>{t('epdWizard.wizardStep2.properties.collections')}</FixedLabelTop>
        <div style={{ width: '100%' }}>
          <InfoBox>
            <img src={helpIcon} alt="information" style={{ height: '35px', marginRight: '1rem' }} />
            <div
              style={{ textAlign: 'justify' }}
              dangerouslySetInnerHTML={{ __html: t('manageCollections.infoDetailed') }}
            />
          </InfoBox>
          <EpdSelectComponent
            options={collectionOptions}
            isMulti
            isDisabled={epd?.collectionState.actionState === ActionState.Disabled || isFetching || isLoadingCollections}
            styles={customSelectStyles}
            isClearable
            value={selectedCollections}
            onChange={changeCollection}
          />
        </div>
      </FixedRow>
      <FixedRow state={epd?.documentsState.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.documents')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.documents')} />
          <Validation validation={validation} epd={epd} field="documents" />
        </FixedLabelTop>
        <div style={{ width: '100%' }}>
          {epd?.documentTemplateUrlState?.actionState !== ActionState.Hidden && (
            <>
              <InfoBox>
                <img src={helpIcon} alt="information" style={{ height: '35px', marginRight: '1rem' }} />
                <div style={{ textAlign: 'justify' }}>
                  {t('epdWizard.wizardStep2.info.documents')}
                  <br />
                  <A href={EPD_TEMPLATES_URL} target="_blank" rel="noopener noreferrer">
                    {t('download.selectTemplate')}
                  </A>
                </div>
              </InfoBox>
              {epd?.isEn15804Compliant ? (
                <InfoBox>
                  <img src={helpIcon} alt="information" style={{ height: '35px', marginRight: '1rem' }} />
                  <div style={{ textAlign: 'justify' }}>
                    {t('epdWizard.wizardStep2.info.logotype')}
                    <br />
                    <A href={process.env.PUBLIC_URL + '/ECO-EPD-logo.zip'} download>
                      {t('download.logotype')}
                    </A>
                  </div>
                  <img
                    style={{ marginTop: '-1rem', width: '8rem', height: '7rem' }}
                    alt="logo"
                    src={process.env.PUBLIC_URL + '/ECO-EPD-Logo.png'}
                  />
                </InfoBox>
              ) : null}
            </>
          )}

          <UploadFile
            accept=".pdf"
            validExtensions={['pdf']}
            title={t('epdWizard.wizardStep2.upload.epdDocuments')}
            uploadFile={uploadDocumentFile}
            disabled={epd?.documentsState.actionState === ActionState.Disabled || fileUploaders.includes('documents')}
            documents={epd?.documents}
            onRemoveFile={onRemoveFile}
            field="Document"
            languageList={languageList}
            documentTypeList={documentTypeList}
            showUploadingIndicator={fileUploaders.includes('documents')}
          />
        </div>
      </FixedRow>
      <EditorFixedRow state={epd?.productDescriptionState.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.productDescription')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.productDescription')} />
          <Validation validation={validation} epd={epd} field="productDescription" />
        </FixedLabelTop>
        <HTMLEditor
          editorStyle={
            epd?.productDescriptionState.actionState === ActionState.Disabled ? hiddenEditorStyle : visibleEditorStyle
          }
          value={epd?.productDescription}
          toolbarHidden={epd?.productDescriptionState.actionState === ActionState.Disabled ? true : false}
          toolbar={customToolbarEpd}
          onEdit={(newValue: string) => onChangeEpd('productDescription', newValue)}
          disabled={epd?.productDescriptionState.actionState === ActionState.Disabled}
          stripPastedStyles={true}
        />
      </EditorFixedRow>
      <FixedRow state={epd?.uncpcCodeState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep2.properties.cpc')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.cpcUnCode')} />
        </FixedLabel>
        <SelectWrapper>
          <EpdSelectComponent
            options={lookupUNCPCCodes}
            isDisabled={epd?.uncpcCodeState.actionState === ActionState.Disabled}
            styles={customSelectStyles}
            isClearable={true}
            defaultValue={{ label: epd?.uncpcCode?.description, value: epd?.uncpcCode?.id }}
            onChange={(newValue: any | Option) => {
              onChangeEpd('uncpcCode', newValue?.value);
            }}
          />
        </SelectWrapper>
      </FixedRow>
      <EditorFixedRow state={epd?.productsState.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.products')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.products')} />
          <Validation validation={validation} epd={epd} field="products" />
        </FixedLabelTop>
        <HTMLEditor
          editorStyle={epd?.productsState.actionState === ActionState.Disabled ? hiddenEditorStyle : visibleEditorStyle}
          value={epd?.products}
          toolbarHidden={epd?.productsState.actionState === ActionState.Disabled ? true : false}
          toolbar={customToolbarEpd}
          onEdit={(newValue: string) => onChangeEpd('products', newValue)}
          disabled={epd?.productsState.actionState === ActionState.Disabled}
        />
      </EditorFixedRow>
      <FixedRow state={epd?.productImagesState.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.images')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.images')} />
          <Validation validation={validation} epd={epd} field="productImages" />
        </FixedLabelTop>
        <div>
          <InfoBox>
            <div>
              <div style={{ marginRight: '1rem', paddingLeft: '0.25rem', alignSelf: 'flex-start' }}></div>
              The image labelled "Cover image" will appear on the product card and on the EPD details page in the EPD
              Library.
            </div>
          </InfoBox>

          <ImagesUpload
            name="images"
            images={epd?.productImages}
            onUpload={(e) => uploadFile(e.target.files?.[0], 'ProductImage', null, null, 'productImages')}
            onReorder={(imgIds: string[]) => {
              EPDService.arrangeEpdFiles(imgIds);
              // Update local state, otherwise after unmounting the images order will be out of sync with DB
              setUpdatedEpd(
                (prev) =>
                  prev && {
                    ...prev,
                    productImages: imgIds
                      .map((id) => prev.productImages?.find((img) => img.epdFileId === id))
                      .filter((img) => !!img),
                  }
              );
            }}
            onRemove={onRemoveFile}
            disabled={epd?.productImagesState.actionState === ActionState.Disabled}
            showUploadingIndicator={fileUploaders.includes('productImages')}
          />
        </div>
      </FixedRow>
      <FixedRow state={epd?.geographicalScopesState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep2.properties.geoScope')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.location')} />
          <Validation validation={validation} epd={epd} field="geographicalScopes" />
        </FixedLabel>
        <SelectWrapper>
          <EpdSelectComponent
            options={locationList}
            isMulti={true}
            isDisabled={epd?.geographicalScopesState.actionState === ActionState.Disabled}
            styles={customSelectStyles}
            isClearable={true}
            defaultValue={geoScopes}
            onChange={(e: MultiValue<Option> | undefined) => changeLocation(e)}
          />
        </SelectWrapper>
      </FixedRow>
      <FixedRow state={epd?.statementOfConformityWithStandardsState.actionState}>
        {showConformityStandardsInfo ? (
          <FixedLabelTop>{conformityStandardsLabel}</FixedLabelTop>
        ) : (
          <FixedLabel>{conformityStandardsLabel}</FixedLabel>
        )}
        <div style={{ width: '100%' }}>
          {showConformityStandardsInfo && <ConformityStandardsInfoBox />}
          <SelectWrapper>
            <EpdSelectComponent
              options={conformityStandardsOptions}
              isMulti={true}
              isDisabled={epd?.statementOfConformityWithStandardsState.actionState === ActionState.Disabled}
              styles={customSelectStyles}
              isClearable={true}
              value={getSelectedStandardOptions(statementOfConformityWithStandards, conformityStandardsOptions)}
              onChange={(e: MultiValue<Option> | undefined) => handleChangestatementOfConformityWith(e)}
            />
          </SelectWrapper>
        </div>
      </FixedRow>
      <H3>{t('epdWizard.wizardStep2.properties.infoReadable')}</H3>
      <h3 style={{ fontWeight: 400, fontSize: '14px' }}>{t('epdWizard.wizardStep2.properties.descriptionReadable')}</h3>
      <FixedRow state={epd?.gtinState.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.gtin')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.gtin')} />
          <Validation validation={validation} epd={epd} field="gtin" />
        </FixedLabelTop>
        <div style={{ width: '100%' }}>
          <InfoBox style={{ alignItems: 'center', padding: '0.5rem 0', marginBottom: 0 }}>
            <div style={{ marginRight: '1rem', paddingLeft: '0.2rem', alignSelf: 'flex-start' }}></div>
            {infoArrowItems.gtin ? (
              <ArrowUpBox>
                <GtinInfo />
              </ArrowUpBox>
            ) : (
              <ArrowDownBox>
                <GtinInfo />
              </ArrowDownBox>
            )}
            {t('epdWizard.wizardStep2.info.gtin').length >= 73 &&
              (infoArrowItems.gtin ? (
                <ArrowImage
                  style={{ paddingRight: '1rem' }}
                  src={arrowUp}
                  alt="Arrow up"
                  onClick={() => toggleOpen('gtin')}
                />
              ) : (
                <ArrowImage
                  style={{ paddingRight: '1rem' }}
                  src={arrowDown}
                  alt="Arrow down"
                  onClick={() => toggleOpen('gtin')}
                />
              ))}
          </InfoBox>
          <div style={{ boxSizing: 'border-box', width: '100%' }}>
            <FixedTextAreaComponent
              value={epd?.gtin}
              propertyName={'gtin'}
              onChangeEpd={onChangeEpd}
              disabled={epd?.gtinState.actionState === ActionState.Disabled}
            />
          </div>
        </div>
      </FixedRow>

      {epd && (
        <WizardStep2LcaResults
          epd={epd}
          onRemoveFile={onRemoveFile}
          setEpdModel={(value) => setUpdatedEpd(value)}
          validation={validation}
        />
      )}
      <FixedRow state={epd?.epdBasedOnUnitState?.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.basedOnUnit')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.basedOnUnit')} />
          <Validation validation={validation} epd={epd} field="epdBasedOnUnit" />
        </FixedLabelTop>
        <div style={{ width: '100%' }}>
          <SelectWrapper>
            <EpdSelectComponent
              value={selectedEPDBasedOnUnit}
              options={epdBasedOnUnitOptions}
              styles={customSelectStyles}
              isDisabled={epd?.epdBasedOnUnitState?.actionState === ActionState.Disabled}
              onChange={(e: { value: any }) =>
                onChangeEpd('epdBasedOnUnit', changeEPDBasedOnUnit(e?.value, selectedFunctionalUnitDescription))
              }
            />
          </SelectWrapper>
        </div>
      </FixedRow>
      <FixedRow state={epd?.functionalUnitDescriptionState?.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.funcUnitDescription')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.funcUnitDesc')} />
          <Validation validation={validation} epd={epd} field="functionalUnitDescription" />
        </FixedLabelTop>
        <div style={{ width: '100%' }}>
          <FixedTextAreaComponent
            value={epd?.functionalUnitDescription}
            propertyName={'functionalUnitDescription'}
            onChangeEpd={(fieldName: string, value: any) => {
              setSelectedFunctionalUnitDescription(value);
              onChangeEpd(fieldName, changeEPDBasedOnUnit(EPDBasedOnUnitEnum.FunctionalUnit, value));
            }}
            disabled={epd?.functionalUnitDescriptionState?.actionState === ActionState.Disabled}
          />
        </div>
      </FixedRow>
      <FixedRow state={epd?.referenceFlowState.actionState}>
        <FixedLabelTop>
          {t(
            `epdCompiler.step3.lcaSpecificationTab.properties.referenceFlow.title.${
              selectedEPDBasedOnUnit?.value ??
              Object.keys(EPDBasedOnUnitEnum).indexOf(EPDBasedOnUnitEnum.DeclaredUnit).toString()
            }`
          )}
          <HelpIcon
            content={t(
              `epdCompiler.step3.lcaSpecificationTab.properties.referenceFlow.tooltip.${
                selectedEPDBasedOnUnit?.value ??
                Object.keys(EPDBasedOnUnitEnum).indexOf(EPDBasedOnUnitEnum.DeclaredUnit).toString()
              }`
            )}
          />
          <Validation validation={validation} epd={epd} field="referenceFlow" />
        </FixedLabelTop>
        <Box style={{ paddingTop: '0.5rem' }}>
          <div style={{ width: '100%', paddingBottom: '1rem' }}>
            <InfoBox style={{ alignItems: 'center', padding: '0.5rem 0', marginBottom: 0, borderLeft: 'none' }}>
              {infoArrowItems.referenceFlows ? (
                <ArrowUpBox>
                  <ReferenceInfo />
                </ArrowUpBox>
              ) : (
                <ArrowDownBox>{t('epdWizard.wizardStep2.info.referenceFlows.ref1')}</ArrowDownBox>
              )}
              {t('epdWizard.wizardStep2.info.referenceFlows.ref1').length >= 73 &&
                (infoArrowItems.referenceFlows ? (
                  <ArrowImage src={arrowUp} alt="Arrow up" onClick={() => toggleOpen('referenceFlows')} />
                ) : (
                  <ArrowImage src={arrowDown} alt="Arrow down" onClick={() => toggleOpen('referenceFlows')} />
                ))}
            </InfoBox>
          </div>
          <ReferenceGrid>
            <ReferenceColumn>
              <Label>{t('epdWizard.wizardStep2.properties.referenceFlows.productFlowName')}</Label>
            </ReferenceColumn>
            <ReferenceColumn>
              <FixedTextInputComponent
                style={productFlowInput}
                value={epd?.referenceFlow?.name}
                propertyName={'productFlow'}
                onChangeEpd={(fieldName: string, value: any) => {
                  onChangeEpd(
                    fieldName,
                    createFlowModel(value ? value : '', selectedFlowPropertyId, selectedConversionFactor)
                  );
                }}
                disabled={epd?.referenceFlowState?.actionState === ActionState.Disabled}
              />
            </ReferenceColumn>
            <Column>
              <Label>{t('epdWizard.wizardStep2.properties.referenceFlows.unit')}</Label>
            </Column>
            <Column>
              <Label>{t('epdWizard.wizardStep2.properties.referenceFlows.conversionFactor')}</Label>
            </Column>
            <Column>
              <EpdSelectComponent
                options={productFlowOptions}
                value={productFlowOptions.find((o) => o.value === selectedFlowPropertyId)}
                isMulti={false}
                isDisabled={epd?.referenceFlowState?.actionState === ActionState.Disabled || isFetching}
                styles={SelectStyle}
                isClearable={true}
                onChange={(event: any) => {
                  changeProductFlowUnit(epd?.referenceFlow?.name ?? null, event?.value ?? null, selectedConversionFactor);
                }}
                {...reactSelectPropsForHtmlLabel}
              />
            </Column>
            <Column>
              <FixedTextInputComponent
                value={selectedConversionFactor}
                propertyName="productFlow"
                type="number"
                min="0.1"
                step="0.1"
                onKeyDown={handleNumericFieldKeyDown}
                onPaste={handleNumericFieldPaste}
                onChange={handleNumericFieldChange}
                onChangeEpd={(_: string, value: number) =>
                  changeProductFlowValue(epd?.referenceFlow?.name ?? null, selectedFlowPropertyId, value)
                }
                disabled={epd?.referenceFlowState?.actionState === ActionState.Disabled || isFetching}
                style={productFlowInput}
                placeholder={t('placeholder.insertNumber')}
              />
            </Column>
          </ReferenceGrid>
        </Box>
      </FixedRow>

      <FixedRow state={epd?.conversionFactorToMassState.actionState}>
        <FixedLabelTop>
          Conversion factor to mass
          <HelpIcon content={t(`epdCompiler.step3.lcaSpecificationTab.properties.conversionFactor.tooltip`)} />
          <ValidationIconWithContainer>
            <Validation validation={validation} epd={epd} field="conversionFactorToMass" />
          </ValidationIconWithContainer>
        </FixedLabelTop>
        <Box style={{ paddingTop: '0.5rem' }}>
          <ReferenceGrid>
            <Column>
              <Label>{t(`epdCompiler.step3.lcaSpecificationTab.properties.conversionFactor.unit`)}</Label>
            </Column>
            <Column>
              <Label>{t(`epdCompiler.step3.lcaSpecificationTab.properties.conversionFactor.value`)}</Label>
            </Column>
            <Column>
              <EpdSelectComponent defaultValue={{ label: '-', value: '-' }} isDisabled={true} styles={SelectStyle} />
            </Column>
            <Column>
              <FixedTextInputComponent
                key={conversionFactorToMassValue}
                value={conversionFactorToMassValue}
                propertyName="conversionFactorToMass"
                type="number"
                step="0.1"
                onKeyDown={handleNumericFieldKeyDown}
                onPaste={handleNumericFieldPaste}
                onChange={handleNumericFieldChange}
                onChangeEpd={(_: string, value: number) =>
                  onChangeEpd('conversionFactorToMass', createConversionFactorToMassModel(value))
                }
                disabled={epd?.conversionFactorToMassState?.actionState === ActionState.Disabled || isFetching}
                style={productFlowInput}
                placeholder={t('placeholder.insertNumber')}
              />
            </Column>
          </ReferenceGrid>
        </Box>
      </FixedRow>

      <FixedRow state={epd?.massOfDeclaredUnitState.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.massOfDeclaredUnit.title')}
          <HelpIcon content={t('epdWizard.wizardStep2.properties.massOfDeclaredUnit.tooltip')} />
          <Validation validation={validation} epd={epd} field="massOfDeclaredUnit" />
        </FixedLabelTop>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          <FixedTextInputComponent
            key={JSON.stringify(epd?.referenceFlow)}
            value={epd?.massOfDeclaredUnit}
            propertyName="massOfDeclaredUnit"
            type="number"
            step="0.1"
            onKeyDown={handleNumericFieldKeyDown}
            onPaste={handleNumericFieldPaste}
            onChange={handleNumericFieldChange}
            onChangeEpd={(name: string, value: string) => onChangeEpd(name, value === '' ? null : value)}
            disabled={epd?.massOfDeclaredUnitState.actionState === ActionState.Disabled || isFetching}
            placeholder={t('placeholder.insertNumber')}
          />
        </div>
      </FixedRow>

      <FixedRow state={epd?.productTypeState?.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.productToDeclare.title')}
          <HelpIcon content={t('epdWizard.wizardStep2.properties.productToDeclare.tooltip')} />
          <Validation validation={validation} epd={epd} field="productType" />
        </FixedLabelTop>
        <div style={{ width: '100%' }}>
          <SelectWrapper>
            <EpdSelectComponent
              value={selectedProductType}
              options={productTypeOptions}
              styles={customSelectStyles}
              isDisabled={epd?.productTypeState?.actionState === ActionState.Disabled || isFetching}
              onChange={(e: { value: any }) => onChangeEpd('productType', e?.value)}
            />
          </SelectWrapper>
        </div>
      </FixedRow>
      {/* US #9798: Temporarily removed everything Okobaudat-related
      
      <FixedRow state={epd?.biogenicCarbonContent?.doesProductContainMoreThan5PercentState?.actionState}>
        <FixedLabelTop>
          Classification for ÖKOBAUDAT
          <HelpIcon
            content={
              'Product category classification on the ÖKOBAUDAT platform. Products need to be classified based on the material they are made from.'
            }
          />
          <Validation
            validation={validation}
            epd={epd}
            field={[
              'requirePublicationOnOkobaudat',
              'okobaudatProductCategoryId',
              'okobaudatProductSubCategoryId',
              'okobaudatProductCategoryTypeId',
            ]}
          />
        </FixedLabelTop>
        <Box style={{ paddingTop: '0.5rem' }}>
          <ReferenceGrid style={{ gridTemplateColumns: '1fr 2fr' }}>
            <ReferenceColumn style={{ display: 'flex' }}>
              <Label style={{ marginRight: 5 }}>{'Require EPD Publication on ÖKOBAUDAT'}</Label>
              <div style={{ marginRight: 5 }}>
                <HelpIcon
                  content={
                    'With the ÖKOBAUDAT platform, the Federal Ministry for Housing, Urban Development and Building (BMWSB) Germany provides all stakeholders with a standardized database for the life cycle assessment of buildings.'
                  }
                />
              </div>
              <Validation validation={validation} epd={epd} field="requirePublicationOnOkobaudat" />
            </ReferenceColumn>
            <ReferenceColumn>
              <EpdSelectComponent
                options={YesNoDataSource}
                value={YesNoDataSource.find((item) => item.value === epd?.requirePublicationOnOkobaudat)}
                isMulti={false}
                isDisabled={epd?.requirePublicationOnOkobaudatState?.actionState === ActionState.Disabled || isFetching}
                styles={SelectStyle}
                isClearable={true}
                onChange={(event: any) => {
                  onChangeEpd('requirePublicationOnOkobaudat', event?.value);
                }}
              />
            </ReferenceColumn>
            <ReferenceColumn style={{ display: 'flex' }}>
              <Label style={{ marginRight: 5 }}>{'Product category'}</Label>
              <Validation validation={validation} epd={epd} field="okobaudatProductCategoryId" />
            </ReferenceColumn>
            <ReferenceColumn>
              <EpdSelectComponent
                options={okobaudatProductCategories?.map((item: OkobaudatProductCategoryModel) => {
                  return {
                    value: item?.id,
                    label: `${item?.number} ${item?.nameEn}`,
                  } as Option;
                })}
                value={
                  selectedProductCategory &&
                  ({
                    value: epd?.okobaudatProductCategoryId,
                    label: `${selectedProductCategory?.number} ${selectedProductCategory?.nameEn}`,
                  } as Option)
                }
                isMulti={false}
                isDisabled={
                  !epd?.requirePublicationOnOkobaudat ||
                  epd?.requirePublicationOnOkobaudat === YES_NO.NO ||
                  epd?.okobaudatProductCategoryIdState.actionState === ActionState.Disabled ||
                  isFetching
                }
                styles={SelectStyle}
                isClearable={true}
                onChange={(event: any) => {
                  onChangeEpd('okobaudatProductCategory', event?.value);
                }}
              />
            </ReferenceColumn>
            <ReferenceColumn style={{ display: 'flex' }}>
              <Label style={{ marginRight: 5 }}>{'Product subcategory'}</Label>
              <Validation validation={validation} epd={epd} field="okobaudatProductSubCategoryId" />
            </ReferenceColumn>
            <ReferenceColumn>
              <EpdSelectComponent
                options={
                  selectedProductCategory
                    ? selectedProductCategory.children?.map((item: OkobaudatProductCategoryModel) => {
                        return {
                          value: item?.id,
                          label: `${item?.number} ${item?.nameEn}`,
                        } as Option;
                      })
                    : undefined
                }
                value={
                  !!selectedSubCategory
                    ? {
                        value: selectedSubCategory?.id,
                        label: `${selectedSubCategory?.number} ${selectedSubCategory?.nameEn}`,
                      }
                    : null
                }
                isMulti={false}
                isDisabled={
                  !epd?.requirePublicationOnOkobaudat ||
                  epd?.requirePublicationOnOkobaudat === YES_NO.NO ||
                  epd?.okobaudatProductSubCategoryIdState.actionState === ActionState.Disabled ||
                  !selectedProductCategory ||
                  isFetching
                }
                styles={SelectStyle}
                isClearable={true}
                onChange={(event: any) => {
                  onChangeEpd('okobaudatProductSubCategory', event?.value);
                }}
              />
            </ReferenceColumn>
            <ReferenceColumn style={{ display: 'flex' }}>
              <Label style={{ marginRight: 5 }}>{'Product category type'}</Label>
              <Validation validation={validation} epd={epd} field="okobaudatProductCategoryTypeId" />
            </ReferenceColumn>
            <ReferenceColumn>
              <EpdSelectComponent
                options={
                  selectedSubCategory
                    ? selectedSubCategory.children?.map((item: OkobaudatProductCategoryModel) => {
                        return {
                          value: item?.id,
                          label: `${item?.number} ${item?.nameEn}`,
                        } as Option;
                      })
                    : undefined
                }
                value={
                  !!selectedCategoryType
                    ? {
                        value: selectedCategoryType?.id,
                        label: `${selectedCategoryType?.number} ${selectedCategoryType?.nameEn}`,
                      }
                    : null
                }
                isMulti={false}
                isDisabled={
                  !epd?.requirePublicationOnOkobaudat ||
                  epd?.requirePublicationOnOkobaudat === YES_NO.NO ||
                  epd?.okobaudatProductCategoryTypeIdState.actionState === ActionState.Disabled ||
                  !selectedSubCategory?.children?.length ||
                  isFetching
                }
                styles={SelectStyle}
                isClearable={true}
                onChange={(event: any) => {
                  onChangeEpd('okobaudatProductCategoryType', event?.value);
                }}
              />
            </ReferenceColumn>
          </ReferenceGrid>
        </Box>
      </FixedRow> */}
      <FixedRow state={epd?.biogenicCarbonContent?.doesProductContainMoreThan5PercentState?.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.biogenicCarbon.title')}
          <HelpIcon content={t('epdWizard.wizardStep2.properties.biogenicCarbon.tooltip')} />
        </FixedLabelTop>
        <Box style={{ paddingTop: '0.5rem' }}>
          <div style={{ width: '100%', paddingBottom: '1rem' }}>
            <InfoBox style={{ alignItems: 'center', padding: '0.5rem 0', marginBottom: 0, borderLeft: 'none' }}>
              {infoArrowItems.biogenicCarbon ? (
                <ArrowUpBox>{t('epdWizard.wizardStep2.properties.biogenicCarbon.infoBox')}</ArrowUpBox>
              ) : (
                <ArrowDownBox>{t('epdWizard.wizardStep2.properties.biogenicCarbon.infoBox')}</ArrowDownBox>
              )}

              {infoArrowItems.biogenicCarbon ? (
                <ArrowImage src={arrowUp} alt="Arrow up" onClick={() => toggleOpen('biogenicCarbon')} />
              ) : (
                <ArrowImage src={arrowDown} alt="Arrow down" onClick={() => toggleOpen('biogenicCarbon')} />
              )}
            </InfoBox>
          </div>
          <ReferenceGrid style={{ gridTemplateColumns: '1fr 2fr' }}>
            <ReferenceColumn style={{ display: 'flex' }}>
              <Label style={{ marginRight: 5 }}>
                {t('epdWizard.wizardStep2.properties.biogenicCarbon.product5percent')}
              </Label>
              <Validation
                validation={validation}
                epd={epd}
                field="biogenicCarbonContent.doesProductContainMoreThan5Percent"
              />
            </ReferenceColumn>
            <ReferenceColumn>
              <EpdSelectComponent
                options={YesNoDataSource}
                value={YesNoDataSource.find(
                  (item) => item.value === epd?.biogenicCarbonContent?.doesProductContainMoreThan5Percent
                )}
                isMulti={false}
                isDisabled={
                  epd?.biogenicCarbonContent?.doesProductContainMoreThan5PercentState?.actionState ===
                    ActionState.Disabled || isFetching
                }
                placeholder={t('placeholder.defaultSelect')}
                styles={SelectStyle}
                isClearable={true}
                onChange={(event: any) => {
                  onChangeEpd(
                    'biogenicCarbonContent',
                    changeBiogenicCarbonContentValues('doesProductContainMoreThan5Percent', event?.value)
                  );
                }}
              />
            </ReferenceColumn>
            <ReferenceColumn style={{ display: 'flex' }}>
              <Label style={{ marginRight: 5 }}>
                {t('epdWizard.wizardStep2.properties.biogenicCarbon.package5percent')}
              </Label>
              <Validation
                validation={validation}
                epd={epd}
                field="biogenicCarbonContent.doesPackagingMaterialContainMoreThan5Percent"
              />
            </ReferenceColumn>
            <ReferenceColumn>
              <EpdSelectComponent
                options={YesNoDataSource}
                value={YesNoDataSource.find(
                  (item) => item.value === epd?.biogenicCarbonContent?.doesPackagingMaterialContainMoreThan5Percent
                )}
                isMulti={false}
                isDisabled={
                  epd?.biogenicCarbonContent?.doesPackagingMaterialContainMoreThan5PercentState?.actionState ===
                    ActionState.Disabled || isFetching
                }
                styles={SelectStyle}
                isClearable={true}
                onChange={(event: any) => {
                  onChangeEpd(
                    'biogenicCarbonContent',
                    changeBiogenicCarbonContentValues('doesPackagingMaterialContainMoreThan5Percent', event?.value)
                  );
                }}
                placeholder={t('placeholder.defaultSelect')}
              />
            </ReferenceColumn>
            <ReferenceColumn style={{ marginTop: 10 }}>
              <Label style={{ marginRight: 5 }}>{t('epdWizard.wizardStep2.properties.biogenicCarbon.product')}</Label>
            </ReferenceColumn>
            <Column style={{ display: 'flex' }}>
              <Label style={{ marginRight: 5 }}>{t('epdWizard.wizardStep2.properties.biogenicCarbon.value')}</Label>
              <Validation validation={validation} epd={epd} field="biogenicCarbonContent.productItem.conversionFactor" />
            </Column>
            <Column style={{ display: 'flex' }}>
              <Label style={{ marginRight: 5 }}>{t('epdWizard.wizardStep2.properties.biogenicCarbon.propertyUnit')}</Label>
              <Validation validation={validation} epd={epd} field="biogenicCarbonContent.productItem.unitKey" />
            </Column>
            <Column>
              <FixedTextInputComponent
                value={epd?.biogenicCarbonContent?.productItem?.conversionFactor}
                propertyName={'productItemConversionFactor'}
                type="number"
                min="0"
                step="any"
                onKeyDown={handleBiogenicKeyDown}
                onPaste={handleBiogenicPaste}
                onChange={handleNumericFieldChange}
                onChangeEpd={(fieldName: string, value: any) => {
                  onChangeEpd(
                    'biogenicCarbonContent',
                    changeBiogenicCarbonContentValues('productItemConversionFactor', value)
                  );
                }}
                disabled={
                  epd?.biogenicCarbonContent?.productItem?.conversionFactorState?.actionState === ActionState.Disabled ||
                  isFetching
                }
                style={productFlowInput}
                placeholder={t('placeholder.insertNumber')}
              />
            </Column>
            <Column>
              <EpdSelectComponent
                options={biogenicCarbonContentUnits}
                value={biogenicCarbonContentUnits.find(
                  (item) => item.value === epd?.biogenicCarbonContent?.productItem?.unitKey?.toString()
                )}
                isMulti={false}
                isDisabled={
                  epd?.biogenicCarbonContent?.productItem?.unitKeyState?.actionState === ActionState.Disabled || isFetching
                }
                styles={SelectStyle}
                isClearable={true}
                onChange={(event: any) => {
                  onChangeEpd(
                    'biogenicCarbonContent',
                    changeBiogenicCarbonContentValues('productItemUnitKey', event?.value)
                  );
                }}
              />
            </Column>
            <ReferenceColumn style={{ marginTop: 10 }}>
              <Label style={{ marginRight: 5 }}>{t('epdWizard.wizardStep2.properties.biogenicCarbon.package')}</Label>
            </ReferenceColumn>
            <Column style={{ display: 'flex' }}>
              <Label style={{ marginRight: 5 }}>{t('epdWizard.wizardStep2.properties.biogenicCarbon.value')}</Label>
              <Validation
                validation={validation}
                epd={epd}
                field="biogenicCarbonContent.accompanyingPackageItem.conversionFactor"
              />
            </Column>
            <Column style={{ display: 'flex' }}>
              <Label style={{ marginRight: 5 }}>{t('epdWizard.wizardStep2.properties.biogenicCarbon.propertyUnit')}</Label>
              <Validation validation={validation} epd={epd} field="biogenicCarbonContent.accompanyingPackageItem.unitKey" />
            </Column>
            <Column>
              <FixedTextInputComponent
                value={epd?.biogenicCarbonContent?.accompanyingPackageItem?.conversionFactor}
                propertyName={'accompanyingPackageItemConversionFactor'}
                type="number"
                min="0"
                step="any"
                onKeyDown={handleBiogenicKeyDown}
                onPaste={handleBiogenicPaste}
                onChange={handleNumericFieldChange}
                onChangeEpd={(fieldName: string, value: any) => {
                  onChangeEpd(
                    'biogenicCarbonContent',
                    changeBiogenicCarbonContentValues('accompanyingPackageItemConversionFactor', value)
                  );
                }}
                disabled={
                  epd?.biogenicCarbonContent?.accompanyingPackageItem?.conversionFactorState?.actionState ===
                    ActionState.Disabled || isFetching
                }
                style={productFlowInput}
                placeholder={t('placeholder.insertNumber')}
              />
            </Column>
            <Column>
              <EpdSelectComponent
                options={biogenicCarbonContentUnits}
                value={biogenicCarbonContentUnits.find(
                  (item) => item.value === epd?.biogenicCarbonContent?.accompanyingPackageItem?.unitKey?.toString()
                )}
                isMulti={false}
                isDisabled={
                  epd?.biogenicCarbonContent?.accompanyingPackageItem?.unitKeyState?.actionState === ActionState.Disabled ||
                  isFetching
                }
                styles={SelectStyle}
                isClearable={true}
                onChange={(event: any) => {
                  onChangeEpd(
                    'biogenicCarbonContent',
                    changeBiogenicCarbonContentValues('accompanyingPackageItemUnitKey', event?.value)
                  );
                }}
              />
            </Column>
          </ReferenceGrid>
        </Box>
      </FixedRow>

      <MaterialPropertiesSection
        epd={epd}
        validation={validation}
        infoArrowItems={infoArrowItems}
        toggleOpen={toggleOpen}
        onChangeEpd={onChangeEpd}
        SelectStyle={SelectStyle}
      />

      <FixedRow state={epd?.geographicalDescriptionState?.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.geoDescription')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.geo')} />
          <Validation validation={validation} epd={epd} field="geographicalDescription" />
        </FixedLabelTop>
        <div style={{ width: '100%' }}>
          <InfoBox style={{ alignItems: 'center', padding: '0.5rem 1rem', marginBottom: 0 }}>
            {infoArrowItems.geo ? (
              <ArrowUpBox>{t('epdWizard.wizardStep2.info.geo')}</ArrowUpBox>
            ) : (
              <ArrowDownBox>{t('epdWizard.wizardStep2.info.geo')}</ArrowDownBox>
            )}
            {t('epdWizard.wizardStep2.info.geo').length >= 73 &&
              (infoArrowItems.geo ? (
                <ArrowImage src={arrowUp} alt="Arrow up" onClick={() => toggleOpen('geo')} />
              ) : (
                <ArrowImage src={arrowDown} alt="Arrow down" onClick={() => toggleOpen('geo')} />
              ))}
          </InfoBox>
          <FixedTextInputComponent
            style={{ boxSizing: 'border-box', width: '100%' }}
            value={epd?.geographicalDescription}
            propertyName={'geographicalDescription'}
            onChangeEpd={onChangeEpd}
            disabled={epd?.geographicalDescriptionState?.actionState === ActionState.Disabled}
          />
        </div>
      </FixedRow>
      <FixedRow state={epd?.technologyDescriptionState?.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.techDescription')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.technology')} />
          <Validation validation={validation} epd={epd} field="technologyDescription" />
        </FixedLabelTop>
        <div style={{ width: '100%' }}>
          <InfoBox style={{ alignItems: 'center', padding: '0.5rem 1rem', marginBottom: 0 }}>
            {infoArrowItems.technology ? (
              <ArrowUpBox>
                <TechInfo />
              </ArrowUpBox>
            ) : (
              <ArrowDownBox>{t('epdWizard.wizardStep2.info.technology.tech1')}</ArrowDownBox>
            )}
            {t('epdWizard.wizardStep2.info.technology').length >= 73 &&
              (infoArrowItems.technology ? (
                <ArrowImage src={arrowUp} alt="Arrow up" onClick={() => toggleOpen('technology')} />
              ) : (
                <ArrowImage src={arrowDown} alt="Arrow down" onClick={() => toggleOpen('technology')} />
              ))}
          </InfoBox>
          <FixedTextAreaComponent
            value={epd?.technologyDescription}
            propertyName={'technologyDescription'}
            onChangeEpd={onChangeEpd}
            disabled={epd?.technologyDescriptionState?.actionState === ActionState.Disabled}
          />
        </div>
      </FixedRow>
      <FixedRow state={epd?.technicalPurposeState?.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.techPurpose')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.technicalPurpose')} />
          <Validation validation={validation} epd={epd} field="technicalPurpose" />
        </FixedLabelTop>
        <div style={{ width: '100%' }}>
          <InfoBox style={{ alignItems: 'center', padding: '0.5rem 1rem', marginBottom: 0 }}>
            {infoArrowItems.technicalPurpose ? (
              <ArrowUpBox>{t('epdWizard.wizardStep2.info.technicalPurpose')}</ArrowUpBox>
            ) : (
              <ArrowDownBox>{t('epdWizard.wizardStep2.info.technicalPurpose')}</ArrowDownBox>
            )}
            {t('epdWizard.wizardStep2.info.technicalPurpose').length >= 73 &&
              (infoArrowItems.technicalPurpose ? (
                <ArrowImage src={arrowUp} alt="Arrow up" onClick={() => toggleOpen('technicalPurpose')} />
              ) : (
                <ArrowImage src={arrowDown} alt="Arrow down" onClick={() => toggleOpen('technicalPurpose')} />
              ))}
          </InfoBox>
          <FixedTextAreaComponent
            value={epd?.technicalPurpose}
            propertyName={'technicalPurpose'}
            onChangeEpd={onChangeEpd}
            disabled={epd?.technicalPurposeState?.actionState === ActionState.Disabled}
          />
        </div>
      </FixedRow>
      <FixedRow state={epd?.subtypeState?.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.subType')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.subType')} />
          <Validation validation={validation} epd={epd} field="subtype" />
        </FixedLabelTop>
        <div style={{ width: '100%' }}>
          <InfoBox style={{ alignItems: 'center', padding: '0.5rem 1rem', marginBottom: 0 }}>
            {t('epdWizard.wizardStep2.info.subType.description').length >= 73 &&
              (infoArrowItems.subType ? (
                <ArrowUpBox>
                  <SubTypeInfo />
                </ArrowUpBox>
              ) : (
                <ArrowDownBox>{t('epdWizard.wizardStep2.info.subType.description')}</ArrowDownBox>
              ))}
            {infoArrowItems.subType ? (
              <ArrowImage src={arrowUp} alt="Arrow up" onClick={() => toggleOpen('subType')} />
            ) : (
              <ArrowImage src={arrowDown} alt="Arrow down" onClick={() => toggleOpen('subType')} />
            )}
          </InfoBox>
          <SelectWrapper>
            <EpdSelectComponent
              value={
                !!epd?.subtype
                  ? subtypeOptions.find((x) => x.value == epd?.subtype?.toString() || '')
                  : {
                      label: '',
                      value: '',
                    }
              }
              options={subtypeOptions}
              styles={customSelectStyles}
              isDisabled={epd?.subtypeState?.actionState === ActionState.Disabled}
              onChange={(e: SingleValue<SubtypeOption> | undefined) => changeSubtype(e)}
            />
          </SelectWrapper>
        </div>
      </FixedRow>

      <DatasourcesSection
        epd={epd}
        validation={validation}
        infoArrowItems={infoArrowItems}
        toggleOpen={toggleOpen}
        onChangeEpd={onChangeEpd}
        SelectStyle={SelectStyle}
        isFetching={isFetching}
      />

      <FixedRow state={epd?.referencePackageVersion15804State.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep2.properties.referencePackageVersion15804')}
          <Validation validation={validation} epd={epd} field="referencePackageVersion15804" />
        </FixedLabel>
        <SelectWrapper>
          <EpdSelectComponent
            options={referencePackageVersions15804Options}
            value={
              epd?.referencePackageVersion15804
                ? referencePackageVersions15804Options.find((o) => o.value === epd.referencePackageVersion15804)
                : null
            }
            isDisabled={epd?.referencePackageVersion15804State.actionState === ActionState.Disabled}
            styles={customSelectStyles}
            onChange={(e: { value: any }) => onChangeEpd('referencePackageVersion15804', e?.value)}
          />
        </SelectWrapper>
      </FixedRow>
      <FixedRow state={epd?.characterisationMethodsState.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.characterisationMethods')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.characterisationMethods')} />
          <Validation validation={validation} epd={epd} field="characterisationMethods" />
        </FixedLabelTop>
        <FixedTextAreaComponent
          value={epd?.characterisationMethods}
          propertyName="characterisationMethods"
          onChangeEpd={onChangeEpd}
          disabled={epd?.characterisationMethodsState.actionState === ActionState.Disabled}
        />
      </FixedRow>

      <FixedRow state={epd?.lcaBasedDescriptionState?.actionState}>
        <FixedLabelTop>
          Additional information about the underlying LCA-based information
          <HelpIcon
            content={
              'This may include information about assumptions, deviations, justification, cut-off rules, data quality, and allocation. This includes also additional information that PCR and c-PCR(s) may require in given circumstances. This field corresponds to the "General Comment on Data Set" section in the ILCD+EPD format.'
            }
          />
        </FixedLabelTop>
        <HTMLEditor
          value={epd?.lcaBasedDescription}
          propertyName="lcaBasedDescription"
          editorStyle={
            epd?.lcaBasedDescriptionState?.actionState === ActionState.Disabled ? hiddenEditorStyle : visibleEditorStyle
          }
          toolbarHidden={epd?.lcaBasedDescriptionState?.actionState === ActionState.Disabled ? true : false}
          toolbar={customToolbarEpd}
          onEdit={(newValue: string) => onChangeEpd('lcaBasedDescription', newValue)}
          disabled={epd?.lcaBasedDescriptionState?.actionState === ActionState.Disabled}
        />
      </FixedRow>

      <FixedRow state={epd?.useAdviceState?.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep2.properties.useAdvice')}
          <HelpIcon content={t('epdWizard.wizardStep2.tooltips.useForDatasets')} />
          <Validation validation={validation} epd={epd} field="useAdvice" />
        </FixedLabelTop>
        <div style={{ width: '100%' }}>
          <InfoBox style={{ alignItems: 'center', padding: '0.5rem 1rem', marginBottom: 0 }}>
            {infoArrowItems.useForDatasets ? (
              <ArrowUpBox>{t('epdWizard.wizardStep2.info.useForDatasets')}</ArrowUpBox>
            ) : (
              <ArrowDownBox>{t('epdWizard.wizardStep2.info.useForDatasets')}</ArrowDownBox>
            )}
            {t('epdWizard.wizardStep2.info.useForDatasets').length >= 73 &&
              (infoArrowItems.useForDatasets ? (
                <ArrowImage src={arrowUp} alt="Arrow up" onClick={() => toggleOpen('useForDatasets')} />
              ) : (
                <ArrowImage src={arrowDown} alt="Arrow down" onClick={() => toggleOpen('useForDatasets')} />
              ))}
          </InfoBox>
          <FixedTextAreaComponent
            value={epd?.useAdvice}
            propertyName={'useAdvice'}
            onChangeEpd={onChangeEpd}
            disabled={epd?.useAdviceState?.actionState === ActionState.Disabled}
          />
        </div>
      </FixedRow>
    </div>
  );
};

const ReferenceGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 2;
  grid-column-gap: 1rem;
  grid-row-gap: 0.5rem;
  width: 100%;
`;

const customSelectStyles = {
  menu: (styles: any) => ({ ...styles }),
  control: (styles: any) => ({
    ...styles,
    borderRadius: 0,
    borderColor: 'gray',
    color: 'black',
    fontSize: '14px',
    minHeight: '49px',
    margin: '0.5rem 0 ',
    width: '100%',
    boxSizing: 'border-box',
  }),
  singleValue: (styles: any, state: any) => ({
    ...styles,
    color: state.isDisabled ? '#545454' : 'black',
  }),
  multiValueLabel: (styles: any, state: any) => ({
    ...styles,
    fontSize: '100%',
    maxWidth: '30rem',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    color: state.isDisabled ? '#545454' : 'black',
  }),
};

export const UploadButton = styled.div`
  position: relative;
  overflow: hidden;
  display: inline-block;
  cursor: pointer;

  & u {
    cursor: pointer;
  }

  input[type='file'] {
    font-size: 100px;
    position: absolute;
    top: 0;
    right: 0;
    text-align: right;
    opacity: 0;
    cursor: pointer;
    display: block;
  }
`;

export const EditorFixedRow = styled(FixedRow)<{ state?: ActionState }>`
  margin-bottom: ${(props) => (props?.state === ActionState.Disabled ? 0 : '3vh')};
`;

const FileBox = styled.div`
  background-color: ${(props) => props.theme.colors.regionColorLight};
  padding: 3rem;
  flex: 1 0 auto;

  > h3 {
    margin-top: 0;
  }
`;

export default WizardStep2;
